import React, { useState, useEffect } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  TrophyIcon,
  UsersIcon,
  AcademicCapIcon,
  MegaphoneIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const Calendar = ({ events, filters, onFilterChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week'
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get first day of the month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const firstDayWeekday = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  // Generate calendar days
  const calendarDays = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayWeekday; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Month names
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Day names
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Event type icons and colors
  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'game':
        return TrophyIcon;
      case 'practice':
        return UsersIcon;
      case 'tournament':
        return TrophyIcon;
      case 'ceremony':
        return AcademicCapIcon;
      case 'tryout':
        return MegaphoneIcon;
      default:
        return CalendarIcon;
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'game':
        return 'bg-blue-100 text-blue-800';
      case 'practice':
        return 'bg-green-100 text-green-800';
      case 'tournament':
        return 'bg-purple-100 text-purple-800';
      case 'ceremony':
        return 'bg-yellow-100 text-yellow-800';
      case 'tryout':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get events for a specific date
  const getEventsForDate = (day) => {
    if (!day) return [];
    
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return events.filter(event => {
      const eventDate = new Date(event.startDate);
      const eventDateStr = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
      return eventDateStr === dateStr;
    });
  };

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  // Format time
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Export functions
  const exportToGoogleCalendar = () => {
    const baseUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE';
    const eventsToExport = selectedDate ? getEventsForDate(selectedDate) : events;
    
    if (eventsToExport.length === 0) return;
    
    // For multiple events, we'll export the first one and provide instructions
    const event = eventsToExport[0];
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    const params = new URLSearchParams({
      text: event.title,
      dates: `${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
      details: event.description,
      location: `${event.location.venue}, ${event.location.address}`
    });
    
    window.open(`${baseUrl}&${params.toString()}`, '_blank');
  };

  const exportToOutlook = () => {
    const eventsToExport = selectedDate ? getEventsForDate(selectedDate) : events;
    
    if (eventsToExport.length === 0) return;
    
    const event = eventsToExport[0];
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    const params = new URLSearchParams({
      subject: event.title,
      startdt: startDate.toISOString(),
      enddt: endDate.toISOString(),
      body: event.description,
      location: `${event.location.venue}, ${event.location.address}`
    });
    
    window.open(`https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`, '_blank');
  };

  const exportToICS = () => {
    const eventsToExport = selectedDate ? getEventsForDate(selectedDate) : events;
    
    if (eventsToExport.length === 0) return;
    
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//East End Prep//Athletics Calendar//EN',
      'CALSCALE:GREGORIAN'
    ];
    
    eventsToExport.forEach(event => {
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      
      icsContent.push(
        'BEGIN:VEVENT',
        `UID:${event.id}@eastendprep.edu`,
        `DTSTART:${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        `DTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description}`,
        `LOCATION:${event.location.venue}, ${event.location.address}`,
        'END:VEVENT'
      );
    });
    
    icsContent.push('END:VCALENDAR');
    
    const blob = new Blob([icsContent.join('\n')], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'eastendprep-athletics.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900">
              {monthNames[currentMonth]} {currentYear}
            </h2>
            <button
              onClick={goToToday}
              className="btn-secondary text-sm"
            >
              Today
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Export Menu */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn-secondary flex items-center space-x-2"
              >
                <DocumentArrowDownIcon className="w-4 h-4" />
                <span>Export</span>
              </button>
              
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        exportToGoogleCalendar();
                        setShowExportMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Google Calendar
                    </button>
                    <button
                      onClick={() => {
                        exportToOutlook();
                        setShowExportMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Outlook
                    </button>
                    <button
                      onClick={() => {
                        exportToICS();
                        setShowExportMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Download .ics
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Navigation */}
            <div className="flex items-center space-x-1">
              <button
                onClick={goToPreviousMonth}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={goToNextMonth}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                <ChevronRightIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-6">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => {
            const dayEvents = day ? getEventsForDate(day) : [];
            const isToday = day && 
              new Date().getDate() === day && 
              new Date().getMonth() === currentMonth && 
              new Date().getFullYear() === currentYear;
            const isSelected = selectedDate === day;

            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 border border-gray-100 cursor-pointer transition-colors ${
                  day ? 'hover:bg-gray-50' : ''
                } ${
                  isSelected ? 'bg-blue-50 border-blue-200' : ''
                } ${
                  isToday ? 'bg-yellow-50 border-yellow-200' : ''
                }`}
                onClick={() => day && setSelectedDate(isSelected ? null : day)}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${
                      isToday ? 'text-yellow-800' : 'text-gray-900'
                    }`}>
                      {day}
                    </div>
                    
                    {/* Events for this day */}
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event, eventIndex) => {
                        const IconComponent = getEventTypeIcon(event.eventType);
                        return (
                          <Link
                            key={event.id}
                            to={`/events/${event.id}`}
                            className="block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className={`text-xs p-1 rounded truncate ${
                              getEventTypeColor(event.eventType)
                            } hover:opacity-80 transition-opacity`}>
                              <div className="flex items-center space-x-1">
                                <IconComponent className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">
                                  {event.title}
                                </span>
                              </div>
                              <div className="text-xs opacity-75 mt-0.5">
                                {formatTime(event.startDate)}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                      
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 text-center py-1">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Events */}
      {selectedDate && (
        <div className="border-t border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Events for {monthNames[currentMonth]} {selectedDate}, {currentYear}
          </h3>
          
          {getEventsForDate(selectedDate).length > 0 ? (
            <div className="space-y-4">
              {getEventsForDate(selectedDate).map(event => {
                const IconComponent = getEventTypeIcon(event.eventType);
                return (
                  <div key={event.id} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    <IconComponent className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Link
                          to={`/events/${event.id}`}
                          className="text-lg font-semibold text-gray-900 hover:text-primary transition-colors"
                        >
                          {event.title}
                        </Link>
                        <span className={`badge ${getEventTypeColor(event.eventType)}`}>
                          {event.eventType}
                        </span>
                        {event.sport && (
                          <span className="badge bg-primary text-white">
                            {event.sport.name}
                          </span>
                        )}
                      </div>
                      
                      {event.opponent && (
                        <p className="text-gray-700 mb-2">vs {event.opponent}</p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          {formatTime(event.startDate)}
                        </div>
                        <div className="flex items-center">
                          <MapPinIcon className="w-4 h-4 mr-1" />
                          {event.location.venue}
                        </div>
                        {event.requiresRSVP && (
                          <span className="badge bg-orange-100 text-orange-800">
                            RSVP Required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No events scheduled for this date.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Calendar;